import React, { useEffect, useState } from 'react'
import { PencilIcon, TrashIcon, PlusIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid'
import { Combobox } from '@headlessui/react'
import Modal from 'react-modal';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { toast } from 'react-toastify';
import useManageAirItemType from './useManageAirItemType';

// Type Selector Component - allows selecting existing type or creating new one
const TypeSelector = ({ value, onChange, options, label, placeholder = "เลือกหรือพิมพ์ประเภทใหม่" }) => {
  const [query, setQuery] = useState(value || '');

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const filteredOptions = query === ''
    ? options
    : options.filter((option) =>
        option.toLowerCase().includes(query.toLowerCase())
      );

  const handleChange = (selectedValue) => {
    if (selectedValue) {
      onChange(selectedValue);
      setQuery(selectedValue);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange(newValue);
  };

  return (
    <Combobox value={value || ''} onChange={handleChange}>
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="relative">
          <Combobox.Input
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onChange={handleInputChange}
            displayValue={() => query}
            placeholder={placeholder}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>
        </div>

        {query && filteredOptions.length > 0 && (
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredOptions.map((option) => (
              <Combobox.Option
                key={option}
                value={option}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-3 pr-9 ${
                    active ? 'bg-blue-600 text-white' : 'text-gray-900'
                  }`
                }
              >
                {({ active, selected }) => (
                  <>
                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                      {option}
                    </span>
                    {selected && (
                      <span
                        className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                          active ? 'text-white' : 'text-blue-600'
                        }`}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}
            {!filteredOptions.includes(query) && query.trim() && (
              <Combobox.Option
                value={query}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-3 pr-9 border-t border-gray-200 ${
                    active ? 'bg-green-600 text-white' : 'text-gray-900'
                  }`
                }
              >
                <span className="block truncate">
                  + สร้าง "{query}"
                </span>
              </Combobox.Option>
            )}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
};

// Customer Price Tiers Editor
// Each tier: { priceRangeMin, priceRangeMax, amount }
// - priceRangeMax '' or null => "and above"
// - priceRangeMin '' or null => "0 and up"
const CustomerPriceTiersEditor = ({ tiers, onChange, unitLabel }) => {
  const list = Array.isArray(tiers) ? tiers : [];

  const updateTier = (index, patch) => {
    const next = list.map((t, i) => (i === index ? { ...t, ...patch } : t));
    onChange(next);
  };

  const removeTier = (index) => {
    onChange(list.filter((_, i) => i !== index));
  };

  const addTier = () => {
    // Suggest next min = previous max
    const last = list[list.length - 1];
    const suggestedMin = last?.priceRangeMax ?? '';
    onChange([
      ...list,
      { priceRangeMin: suggestedMin, priceRangeMax: '', amount: '' },
    ]);
  };

  return (
    <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-md">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-emerald-800">
          ช่วงราคาลูกค้า (Customer Price Tiers) — สำหรับ pre-calculator
        </p>
        <button
          type="button"
          onClick={addTier}
          className="text-xs bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-700 flex items-center gap-1"
        >
          <PlusIcon className="h-3 w-3" /> เพิ่มช่วง
        </button>
      </div>
      <p className="text-xs text-gray-600 mb-2">
        ตัวอย่าง: 0–1000 JPY = 400 บาท/{unitLabel}, 1000–2000 JPY = 500 บาท/{unitLabel}, 2000+ JPY = 700 บาท/{unitLabel}
        <br />
        เว้นว่าง &quot;ราคาสูงสุด&quot; = ไม่จำกัด (ขึ้นไป). ถ้าไม่มีรายการ = ใช้ราคา Normal เป็น flat rate
      </p>
      {list.length === 0 && (
        <p className="text-xs text-gray-500 italic">ยังไม่มีช่วงราคา — จะใช้ราคา Normal เป็นค่าเริ่มต้น</p>
      )}
      {list.map((tier, index) => (
        <div key={index} className="grid grid-cols-12 gap-2 items-end mb-2">
          <div className="col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              ราคาขั้นต่ำ (JPY)
            </label>
            <input
              type="number"
              min="0"
              placeholder="0"
              value={tier.priceRangeMin ?? ''}
              onChange={(e) =>
                updateTier(index, {
                  priceRangeMin: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                })
              }
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            />
          </div>
          <div className="col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              ราคาสูงสุด (JPY)
            </label>
            <input
              type="number"
              min="0"
              placeholder="ว่าง = ขึ้นไป"
              value={tier.priceRangeMax ?? ''}
              onChange={(e) =>
                updateTier(index, {
                  priceRangeMax: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                })
              }
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            />
          </div>
          <div className="col-span-5">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              ราคาที่ชาร์จ (THB / {unitLabel})
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="เช่น 400"
              value={tier.amount ?? ''}
              onChange={(e) =>
                updateTier(index, {
                  amount: e.target.value === '' ? '' : parseFloat(e.target.value),
                })
              }
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            />
          </div>
          <div className="col-span-1">
            <button
              type="button"
              onClick={() => removeTier(index)}
              className="text-red-600 hover:text-red-800 p-1.5 border border-red-200 rounded hover:bg-red-50"
              title="ลบช่วงนี้"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Simplified Form Component (no rules)
const AirItemTypeForm = ({
  airItemType,
  setAirItemType,
  handleInputChange,
  handleRegister,
  handleUpdate,
  mode,
  modalTitle,
  closeForm,
  uniqueCategories = [],
  uniqueTypes = []
}) => {

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate sendOnlyAmountPerUnit
    const sendOnlyVal = airItemType.sendOnlyAmountPerUnit;
    if (sendOnlyVal === '' || sendOnlyVal === null || sendOnlyVal === undefined) {
      toast.error('กรุณากรอกราคา SendOnly');
      return;
    }
    const sendOnlyNum = parseFloat(sendOnlyVal);
    if (isNaN(sendOnlyNum) || sendOnlyNum < 0) {
      toast.error('ราคา SendOnly ต้องเป็นตัวเลขที่ถูกต้อง');
      return;
    }

    // Normalize costRules: only send when cost is per_kg and required fields are valid
    let costRules = null;
    if (airItemType.costCalculationMethod === 'per_kg' && airItemType.costRules) {
      const r = airItemType.costRules;
      const firstKg = r.firstKg !== '' && r.firstKg != null ? parseFloat(r.firstKg) : NaN;
      const amountPerKgOver = r.amountPerKgOver !== '' && r.amountPerKgOver != null ? parseFloat(r.amountPerKgOver) : NaN;
      const useFixedFirstStep = r.firstStepIsFixed === true;
      if (!Number.isNaN(firstKg) && !Number.isNaN(amountPerKgOver)) {
        if (useFixedFirstStep) {
          const firstStepAmount = r.firstStepAmount !== '' && r.firstStepAmount != null ? parseFloat(r.firstStepAmount) : NaN;
          if (!Number.isNaN(firstStepAmount)) costRules = { firstStepAmount, firstKg, amountPerKgOver };
        } else {
          const amountPerKgFirst = r.amountPerKgFirst !== '' && r.amountPerKgFirst != null ? parseFloat(r.amountPerKgFirst) : NaN;
          if (!Number.isNaN(amountPerKgFirst)) costRules = { firstKg, amountPerKgFirst, amountPerKgOver };
        }
      }
    }

    const minBill =
      airItemType.calculationMethod === 'per_kg' &&
      airItemType.minimumBillingWeightKg !== '' &&
      airItemType.minimumBillingWeightKg != null
        ? parseFloat(airItemType.minimumBillingWeightKg)
        : null;
    if (minBill !== null && (Number.isNaN(minBill) || minBill < 0)) {
      toast.error('น้ำหนักขั้นต่ำชาร์จ (kg) ต้องเป็นตัวเลข ≥ 0');
      return;
    }

    // Normalize customerPriceTiers: drop invalid rows, sort by min asc.
    // amount is required; min/max optional (null allowed). If nothing valid, send empty array.
    const customerPriceTiers = Array.isArray(airItemType.customerPriceTiers)
      ? airItemType.customerPriceTiers
          .map((t) => {
            const amountNum = t.amount === '' || t.amount == null ? NaN : parseFloat(t.amount);
            if (Number.isNaN(amountNum) || amountNum < 0) return null;
            const minNum = t.priceRangeMin === '' || t.priceRangeMin == null
              ? null
              : parseInt(t.priceRangeMin, 10);
            const maxNum = t.priceRangeMax === '' || t.priceRangeMax == null
              ? null
              : parseInt(t.priceRangeMax, 10);
            return {
              priceRangeMin: Number.isNaN(minNum) ? null : minNum,
              priceRangeMax: Number.isNaN(maxNum) ? null : maxNum,
              amount: amountNum,
            };
          })
          .filter(Boolean)
          .sort((a, b) => (a.priceRangeMin ?? -Infinity) - (b.priceRangeMin ?? -Infinity))
      : [];

    // Validate tiers: max must be >= min when both set
    for (const t of customerPriceTiers) {
      if (t.priceRangeMin != null && t.priceRangeMax != null && t.priceRangeMax < t.priceRangeMin) {
        toast.error('ช่วงราคาลูกค้า: ราคาสูงสุดต้องมากกว่าหรือเท่ากับราคาขั้นต่ำ');
        return;
      }
    }

    const payload = {
      ...airItemType,
      // Allow admin to create item type without knowing cost yet.
      // Convention: costAmountPerUnit = 0 means "needs cost".
      costAmountPerUnit:
        airItemType.costAmountPerUnit === '' ||
        airItemType.costAmountPerUnit === null ||
        airItemType.costAmountPerUnit === undefined
          ? 0
          : airItemType.costAmountPerUnit,
      costRules,
      minimumBillingWeightKg: minBill,
      customerPriceTiers,
    };

    if (mode === 'edit') {
      await handleUpdate(payload);
    } else {
      await handleRegister(payload);
    }
  };

  const unitLabel = airItemType.calculationMethod === 'per_kg' ? 'kg' : 'ชิ้น';
  
  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">{modalTitle}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Fields - 3 Level: Category > Type > TypeName */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <TypeSelector
              value={airItemType.category || ''}
              onChange={(value) => setAirItemType({ ...airItemType, category: value })}
              options={uniqueCategories}
              label="Category (หมวดหมู่)"
              placeholder="เช่น clothing, bag, golf"
            />
          </div>
          <div>
            <TypeSelector
              value={airItemType.type || ''}
              onChange={(value) => setAirItemType({ ...airItemType, type: value })}
              options={uniqueTypes}
              label="Type (ประเภท)"
              placeholder="เช่น hat, shoes, tote"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type Name (ชื่อ)</label>
            <input
              type="text"
              name="typeName"
              value={airItemType.typeName || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="เช่น Hat - Brand Name 35-225k"
              required
            />
          </div>
        </div>

        {/* Brand tier & Price range (JPY) - for matching/suggestion in quote modal */}
        <div className="border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Brand tier & ช่วงราคา (JPY)</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Brand tier</label>
              <select
                name="brandTier"
                value={airItemType.brandTier || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {BRAND_TIER_OPTIONS.map((opt) => (
                  <option key={opt.value || 'empty'} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ราคาต่ำสุด (JPY)</label>
              <input
                type="number"
                name="priceRangeMin"
                value={airItemType.priceRangeMin ?? ''}
                onChange={handleInputChange}
                min="0"
                placeholder="เช่น 35000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ราคาสูงสุด (JPY)</label>
              <input
                type="number"
                name="priceRangeMax"
                value={airItemType.priceRangeMax ?? ''}
                onChange={handleInputChange}
                min="0"
                placeholder="เช่น 225000 หรือว่าง = ขึ้นไป"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
        </div>

        {/* Customer Pricing */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-2">ราคาลูกค้า</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">วิธีการคำนวณ</label>
              <select
                name="calculationMethod"
                value={airItemType.calculationMethod || 'per_kg'}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="per_kg">ราคาต่อกิโลกรัม</option>
                <option value="per_item">ราคาต่อชิ้น</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ราคา Normal (THB/{unitLabel})</label>
              <input
                type="number"
                name="amountPerUnit"
                value={airItemType.amountPerUnit || ''}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ราคา SendOnly (THB/{unitLabel}) <span className="text-red-600">*</span></label>
              <input
                type="number"
                name="sendOnlyAmountPerUnit"
                value={airItemType.sendOnlyAmountPerUnit ?? ''}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="กรอกแม้จะเท่าการซื้อปกติ"
              />
            </div>
            {airItemType.calculationMethod === 'per_kg' && (
              <div className="col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  น้ำหนักขั้นต่ำชาร์จ (kg) — ทับค่าในโปรไฟล์ลูกค้า
                </label>
                <input
                  type="number"
                  name="minimumBillingWeightKg"
                  value={airItemType.minimumBillingWeightKg ?? ''}
                  onChange={handleInputChange}
                  step="0.001"
                  min="0"
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="ว่าง = ใช้ minimum จากโปรไฟล์ลูกค้า (เช่น 0.1)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ใช้เมื่อรายการนี้ต้องการขั้นต่ำต่ำกว่า/ต่างจากลูกค้า (เช่น ชาร์จอย่างน้อย 0.1 kg แทน 1 kg)
                </p>
              </div>
            )}
          </div>

          <CustomerPriceTiersEditor
            tiers={airItemType.customerPriceTiers || []}
            onChange={(next) =>
              setAirItemType({ ...airItemType, customerPriceTiers: next })
            }
            unitLabel={unitLabel}
          />
        </div>

        {/* Cost Pricing */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-2">ต้นทุน</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">วิธีการคำนวณ</label>
              <select
                name="costCalculationMethod"
                value={airItemType.costCalculationMethod || 'per_kg'}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="per_kg">ราคาต่อกิโลกรัม</option>
                <option value="per_item">ราคาต่อชิ้น</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ราคาต้นทุน (THB)</label>
              <input
                type="number"
                name="costAmountPerUnit"
                value={airItemType.costAmountPerUnit ?? ''}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="เว้นว่างได้ (ระบบจะตั้งเป็น 0 เพื่อให้มาเติมทีหลัง)"
              />
              <p className="text-xs text-amber-700 mt-1">
                ถ้าเว้นว่าง ระบบจะบันทึกเป็น <span className="font-semibold">0</span> และจะแสดงป้าย <span className="font-semibold">ต้องใส่ต้นทุน</span> ในตาราง
              </p>
            </div>
          </div>
          {/* Optional tiered cost rule (cost only, per_kg) */}
          {airItemType.costCalculationMethod === 'per_kg' && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm font-medium text-amber-800 mb-2">กฎต้นทุนแบบขั้น (ถ้ามี) — เฉพาะต้นทุน per kg</p>
              {(() => {
                const r = airItemType.costRules || {};
                const isFixed = r.firstStepIsFixed === true || (r.firstStepAmount != null && r.firstStepAmount !== '');
                return (
                  <div className="flex flex-col gap-2 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="firstStepType"
                        checked={!isFixed}
                        onChange={() => setAirItemType({
                          ...airItemType,
                          costRules: { ...r, firstStepIsFixed: false, firstStepAmount: undefined }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">ขั้นแรกต่อ kg (เช่น แรก 1 kg ที่ 600/kg → 0.8 kg = 480)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="firstStepType"
                        checked={!!isFixed}
                        onChange={() => setAirItemType({
                          ...airItemType,
                          costRules: { ...r, firstStepIsFixed: true }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">ขั้นแรกคงที่ (เช่น แรก 1 kg = 600 บาท → 0.8 kg = 600, 1.5 kg = 600 + 0.5×200 = 700)</span>
                    </label>
                  </div>
                );
              })()}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">น้ำหนักขั้นแรก (kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="1"
                    value={airItemType.costRules?.firstKg ?? ''}
                    onChange={(e) => setAirItemType({
                      ...airItemType,
                      costRules: { ...(airItemType.costRules || {}), firstKg: e.target.value === '' ? '' : parseFloat(e.target.value) }
                    })}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  />
                </div>
                {airItemType.costRules?.firstStepIsFixed ? (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">บาท (ขั้นแรกคงที่)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="600"
                      value={airItemType.costRules?.firstStepAmount ?? ''}
                      onChange={(e) => setAirItemType({
                        ...airItemType,
                        costRules: { ...(airItemType.costRules || {}), firstStepAmount: e.target.value === '' ? '' : parseFloat(e.target.value) }
                      })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">บาท/kg (ขั้นแรก)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="600"
                      value={airItemType.costRules?.amountPerKgFirst ?? ''}
                      onChange={(e) => setAirItemType({
                        ...airItemType,
                        costRules: { ...(airItemType.costRules || {}), amountPerKgFirst: e.target.value === '' ? '' : parseFloat(e.target.value) }
                      })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">บาท/kg (ส่วนเกิน)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="200"
                    value={airItemType.costRules?.amountPerKgOver ?? ''}
                    onChange={(e) => setAirItemType({
                      ...airItemType,
                      costRules: { ...(airItemType.costRules || {}), amountPerKgOver: e.target.value === '' ? '' : parseFloat(e.target.value) }
                    })}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">เว้นว่างทุกช่อง = ใช้ราคาต้นทุนธรรมดา (ไม่มีกฎขั้น)</p>
            </div>
          )}
        </div>

        {/* Other Fields */}
        <div className="border-t pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            name="description"
            value={airItemType.description || ''}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            rows="2"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="isActive"
              checked={airItemType.isActive || false}
              onChange={handleInputChange}
              className="w-4 h-4"
            />
            <span className="text-sm font-medium text-gray-700">Is Active</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="isCustomerVisible"
              checked={airItemType.isCustomerVisible || false}
              onChange={handleInputChange}
              className="w-4 h-4"
            />
            <span className="text-sm font-medium text-emerald-700">
              แสดงบนหน้า Pre-Calculator ของลูกค้า (Customer Visible)
            </span>
          </label>
          <p className="text-xs text-gray-500 ml-6">
            ติ๊กเฉพาะ 1 รายการต่อ (Category + Type) เท่านั้น —
            ใช้เป็นตัวแทนของ type นั้นบนหน้าคำนวณราคาฝั่งลูกค้า และตั้งค่า Customer Price Tiers ที่รายการนี้
          </p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-2 pt-4 border-t">
          <button
            type="button"
            onClick={closeForm}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            {mode === 'edit' ? 'Update' : 'Register'}
          </button>
        </div>
      </form>
    </div>
  );
};

const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      height: 'auto',
      maxHeight: '90vh',
      width: '800px',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      overflowY: 'auto',
    },
  };

const BRAND_TIER_OPTIONS = [
  { value: '', label: '— ไม่ระบุ —' },
  { value: 'normal', label: 'Normal' },
  { value: 'street_brand', label: 'Street' },
  { value: 'brand_name', label: 'Brand Name' },
];

const initialValue = {
  id: '',
  category: '',
  type: '',
  typeName: '',
  brandTier: '',
  priceRangeMin: '',
  priceRangeMax: '',
  calculationMethod: 'per_kg',
  amountPerUnit: '',
  sendOnlyAmountPerUnit: null,
  minimumBillingWeightKg: '',
  costCalculationMethod: 'per_kg',
  costAmountPerUnit: '',
  costRules: null, // optional: { firstKg, amountPerKgFirst, amountPerKgOver } for tiered cost (cost only)
  customerPriceTiers: [], // customer pre-calculator tiers: [{ priceRangeMin, priceRangeMax, amount }]
  isCustomerVisible: false, // show this row on the customer pre-calculator page
  description: '',
  isActive: true,
}

const AirItemTypeSetting = () => {

  const [mode, setMode] = useState('register')
  const [modalTitle, setModalTitle] = useState('Register Air Item Type')

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [airItemType, setAirItemType] = useState(initialValue);
  const [filterCategory, setFilterCategory] = useState('');
  const [customerVisibleOnly, setCustomerVisibleOnly] = useState(false);

  const {
    airItemTypes,
    uniqueCategories,
    uniqueTypes,
    isLoading,
    error,
    addAirItemType,
    editAirItemType,
    deleteAirItemType,
    refreshAirItemTypes,
    refreshUniqueCategories,
    refreshUniqueTypes
  } = useManageAirItemType();

  const handleInputChange = (e) => {
    const {name, value, type, checked} = e.target;
    setAirItemType({
      ...airItemType, 
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const openForm = async (isEdit, id) => {
    if(isEdit){
      const airItemTypeToEdit = airItemTypes.find(item => item.id === id);
      if(airItemTypeToEdit) {
        setAirItemType({
          ...airItemTypeToEdit,
          customerPriceTiers: Array.isArray(airItemTypeToEdit.customerPriceTiers)
            ? airItemTypeToEdit.customerPriceTiers
            : [],
        });
        setModalTitle('Update Air Item Type');
        setMode('edit');
      }
    }else{
      setAirItemType(initialValue)
      setMode('register')
      setModalTitle('Register Air Item Type')
    }
    setIsFormOpen(true);
  };

  const closeForm = () =>{
    setIsFormOpen(false);
  }

  const openConfirm = async (id) => {
    const airItemTypeToDelete = airItemTypes.find(item => item.id === id);
    if(airItemTypeToDelete) {
      setAirItemType(airItemTypeToDelete);
      setIsConfirmOpen(true)
    }
  };

  const closeConfirm = () =>{
    setIsConfirmOpen(false)
  }

  const handleRegisterAirItemType = async (data) => {
    try {
        const returnResult = await addAirItemType(data);
        if (returnResult) {
          toast.success('Air Item Type registered successfully!');
          refreshUniqueCategories();
          refreshUniqueTypes();
          closeForm();
        } else {
          toast.error(error);
        }
    } catch (err) {
      toast.error('An error occurred while registering air item type');
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleUpdateAirItemType = async (data) => {
    try {
      await editAirItemType(data.id, data);
      toast.success('Air Item Type updated successfully!');
      refreshUniqueCategories();
      refreshUniqueTypes();
      closeForm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  const handleDeleteAirItemType = async (id) => {
    try {
      await deleteAirItemType(id);
      toast.success('Air Item Type deleted successfully!');
      refreshUniqueCategories();
      refreshUniqueTypes();
      closeConfirm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    refreshUniqueCategories();
    refreshUniqueTypes();
  }, []);

  useEffect(() => {
    const filters = {};
    if (filterCategory && filterCategory.trim()) {
      filters.category = filterCategory.trim();
    }
    if (customerVisibleOnly) {
      filters.isCustomerVisible = 'true';
    }
    refreshAirItemTypes(filters);
  }, [filterCategory, customerVisibleOnly]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className='text-5xl'>ค่าส่งแอร์รายชนิด</h2>
        <button
          onClick={() => openForm(false)}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          เพิ่มรายการใหม่
        </button>
      </div>

      {/* Filter */}
      <div className="mb-4 flex items-end gap-4 flex-wrap">
        <div className="max-w-xs w-64">
          <TypeSelector
            value={filterCategory}
            onChange={(value) => setFilterCategory(value)}
            options={uniqueCategories}
            label="กรองตาม Category"
            placeholder="ทั้งหมด - เลือกหรือพิมพ์เพื่อกรอง"
          />
        </div>
        <label className="flex items-center gap-2 mb-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={customerVisibleOnly}
            onChange={(e) => setCustomerVisibleOnly(e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm font-medium text-emerald-700">
            แสดงเฉพาะรายการ Customer Visible
          </span>
        </label>
        {(filterCategory || customerVisibleOnly) && (
          <button
            onClick={() => { setFilterCategory(''); setCustomerVisibleOnly(false); }}
            className="mb-2 text-sm text-blue-600 hover:text-blue-800 underline"
          >
            ล้างตัวกรอง
          </button>
        )}
      </div>

      {/* Loader */}
      {isLoading && (
          <div className="flex justify-center items-center h-60">
              <div className="loader"></div>
          </div>
      )}

      {/* Error Message */}
      {error && (
          <div className="my-4 p-4 bg-red-100 text-red-500 border border-red-400 rounded">
              An error occurred: {error}
          </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                หมวดหมู่
              </th>
              <th className="px-4 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ประเภท
              </th>
              <th className="px-4 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ชื่อ
              </th>
              <th className="px-4 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Brand / ช่วงราคา
              </th>
              <th className="px-4 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ราคาลูกค้า
              </th>
              <th className="px-4 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ต้นทุน
              </th>
              <th className="px-4 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                สถานะ
              </th>
              <th className="px-4 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                การดำเนินการ
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {airItemTypes && airItemTypes.length > 0 && airItemTypes.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.category || '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.type}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {item.typeName}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.brandTier ? (
                    <span>
                      {BRAND_TIER_OPTIONS.find(o => o.value === item.brandTier)?.label || item.brandTier}
                      {(item.priceRangeMin != null || item.priceRangeMax != null) && (
                        <span className="text-gray-400">
                          {' '}
                          {item.priceRangeMin != null ? `${(item.priceRangeMin / 1000).toFixed(0)}k` : '?'}
                          –
                          {item.priceRangeMax != null ? `${(item.priceRangeMax / 1000).toFixed(0)}k` : '+'}
                        </span>
                      )}
                    </span>
                  ) : '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex flex-col">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full mb-1 w-fit ${
                      item.calculationMethod === 'per_kg' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {item.calculationMethod === 'per_kg' ? 'ต่อ kg' : 'ต่อชิ้น'}
                    </span>
                    <span className="font-medium text-green-700">
                      Normal: {item.amountPerUnit ? `฿${parseFloat(item.amountPerUnit).toLocaleString()}` : '-'}
                    </span>
                    <span className="font-medium text-blue-700">
                      SendOnly: {item.sendOnlyAmountPerUnit ? `฿${parseFloat(item.sendOnlyAmountPerUnit).toLocaleString()}` : '-'}
                    </span>
                    {item.calculationMethod === 'per_kg' && item.minimumBillingWeightKg != null && item.minimumBillingWeightKg !== '' && (
                      <span className="text-xs text-amber-800 mt-0.5">
                        ขั้นต่ำชาร์จ: {parseFloat(item.minimumBillingWeightKg)} kg (ทับโปรไฟล์)
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex flex-col">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full mb-1 w-fit ${
                      item.costCalculationMethod === 'per_kg' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {item.costCalculationMethod === 'per_kg' ? 'ต่อ kg' : 'ต่อชิ้น'}
                    </span>
                    {Number(item.costAmountPerUnit) === 0 && (
                      <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 w-fit mb-1">
                        ต้องใส่ต้นทุน
                      </span>
                    )}
                    <span className="font-medium">
                      {item.costAmountPerUnit != null && item.costAmountPerUnit !== ''
                        ? `฿${parseFloat(item.costAmountPerUnit).toLocaleString()}`
                        : '-'}
                    </span>
                    {item.costRules && item.costRules.firstKg != null && item.costRules.amountPerKgOver != null && (
                      <span className="text-xs text-amber-700 mt-0.5">
                        {item.costRules.firstStepAmount != null
                          ? `กฎขั้น: แรก ${item.costRules.firstKg} kg = ฿${Number(item.costRules.firstStepAmount).toLocaleString()} ส่วนเกิน ฿${Number(item.costRules.amountPerKgOver).toLocaleString()}/kg`
                          : `กฎขั้น: แรก ${item.costRules.firstKg} kg ที่ ฿${Number(item.costRules.amountPerKgFirst).toLocaleString()}/kg ส่วนเกิน ฿${Number(item.costRules.amountPerKgOver).toLocaleString()}/kg`}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex flex-col gap-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full w-fit ${
                      item.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {item.isActive ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                    </span>
                    {item.isCustomerVisible && (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 w-fit">
                        Customer
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openForm(true, item.id)}
                      className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                      title="แก้ไข"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openConfirm(item.id)}
                      className="text-red-600 hover:text-red-900 p-1 rounded"
                      title="ลบ"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {(!airItemTypes || airItemTypes.length === 0) && (
              <tr>
                <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                  ไม่มีข้อมูลรายการค่าส่งแอร์
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isFormOpen}
        onRequestClose={closeForm}
        style={customStyles}
        ariaHideApp={false}
      >
        <AirItemTypeForm
          airItemType={airItemType}
          setAirItemType={setAirItemType}
          handleInputChange={handleInputChange}
          handleRegister={handleRegisterAirItemType}
          handleUpdate={handleUpdateAirItemType}
          mode={mode}
          modalTitle={modalTitle}
          closeForm={closeForm}
          uniqueCategories={uniqueCategories}
          uniqueTypes={uniqueTypes}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={closeConfirm}
        style={customStyles}
        ariaHideApp={false}
      >
        <DeleteConfirm 
          titleText={`ยืนยันลบ "${airItemType?.typeName ?? 'Air Item Type'}" ?`}
          data={airItemType}
          closeConfirm={closeConfirm}
          confirmFunction={() => handleDeleteAirItemType(airItemType?.id)}
        />
      </Modal>
    </div>
  );
};

export default AirItemTypeSetting;
